"use client";
import { ServiceSummary } from "@/app/(shop)/services/[slug]/components/serviceSummary";
import { useMerchant } from "@/app/providers/merchantProvider";
import Divider from "@/components/divider/divider";
import { EditAddressesModal } from "@/components/editAddressesModal/editAddressesModal";
import { MerchantLogo } from "@/components/header/merchantLogo";
import { IconSkeleton } from "@/components/loadingSkeleton/loadingSkeleton";
import { ScanToOrderKitchenModal } from "@/components/scanToOrder/scanToOrderCart/scanToOrderKitchenModal";
import { OrderType } from "@/constants/enum";
import { useCart } from "@/contexts/CartContext";
import { useCartDrawer } from "@/contexts/CartDrawerContext";
import { onDeliveryAddressUpdate } from "@/dataLayer/deliveryAddressUtils";
import { CustomerDetails } from "@/dataLayer/getCustomerDetails";
import { Address } from "@/dto/address";
import { Category } from "@/dto/category";
import {
  DayPart,
  DayPartMenuCategories,
} from "@/dto/dayPartMenu";
import { GiftCard } from "@/dto/giftCard";
import { TableData } from "@/dto/tableData";
import { RestaurantCart } from "@/models/cart/restaurantCart";
import { RetailCart } from "@/models/cart/retailCart";
import { ServiceCart } from "@/models/cart/serviceCart";
import {
  FEATURE_FLAG_HORIZONTAL_SCROLL_ON_COLO2,
  ROUTE_ALL,
  ROUTE_HOME,
  ROUTE_RETAIL,
  ROUTE_SERVICES,
  ROUTE_SHOP,
  ROUTE_UNCATEGORISED,
  UNCATEGORISED_CATEGORY_ID,
} from "@/utils/constants";
import {
  buildNavQueryParams,
  getCartAriaLabel,
  getMappedNavigationCategories,
  getShowPerks,
  insertRecommendedCategory,
  insertUncategorisedCategory,
  shouldShowMoreDropdown,
  shouldShowPerksPanel,
} from "@/utils/navigationBarUtils";
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from "@/utils/localStorageUtils";
import pom from "@/utils/pom";
import { getCartStatus } from "@/utils/restaurantCartUtils";
import { LocalStorageKey } from "@/utils/storageKeys";
import useLocalStorage from "@/utils/useLocalStorage";
import useMediaQuery from "@/utils/useMediaQuery";
import { useMerchantFeatureFlag } from "@/utils/useMerchantFeatureFlag";
import { applyDataTestId } from "@/utils/testUtils";
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from "@headlessui/react";
import { Bars3Icon, ChevronDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  use,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCookies } from "react-cookie";
import { RetailCartDrawer } from "../cartDrawer/retailCartDrawer";
import {
  ClearCartAction,
  ClearCartModal,
} from "../clearCartModal/clearCartModal";
import { DeliveryModalError } from "../deliveryModalError/deliveryModalError";
import { CartIcon } from "../header/cartIcon";
import { BusinessHoursModal } from "../header/hoursModal";
import { MobileCategoryModal } from "../header/mobileCategoryModal";
import { RestaurantCartDrawer } from "../restaurantCartDrawer/restaurantCartDrawer";
import { ScanToOrderCart } from "../scanToOrder/scanToOrderCart/scanToOrderCart";
import { CartNumber } from "./cartNumber";
import { CategoryButton } from "./categoryButton";
import { DayPartMenuSelector } from "./dayPartMenuSelector";
import { MobileNavigationBar } from "./mobileNavigationBar";
import { NavLink } from "./navLink";
import { Perks } from "./perks";
import { SearchField } from "./searchField";
import { SearchIcon } from "./searchIcon";
import { SecondaryNavigation } from "./secondaryNavigation";
import { UtilityPanel } from "./utilityPanel";
import { useNativeActions } from "@/utils/useNativeActions";
import { useAuth } from "@/app/providers/authProvider";
import { useDepositPolicy } from "@/utils/useDepositPolicy";
import { useMerchantColor } from "@/utils/hooks/useMerchantColor";
import { useAuthLifecycle } from "@/utils/hooks/useAuthLifecycle";
import { useCartDrawerRedirects } from "@/utils/hooks/useCartDrawerRedirects";
import { useNavSearch } from "@/utils/hooks/useNavSearch";
import { useNavigationBarEffects } from "@/utils/hooks/useNavigationBarEffects";
import { useNavigationBarHandlers } from "@/utils/hooks/useNavigationBarHandlers";
import { useOrderEstimatePolling } from "@/utils/hooks/useOrderEstimatePolling";
import { shouldHardNavigate } from "@/utils/hardNavigation";
import { useRecommendedCategory } from "@/contexts/RecommendedCategoryContext";

interface CartVisibilityConfig {
  shouldRenderRetailCart: boolean;
  shouldRenderRestaurantCart: boolean;
  showPerks: boolean;
  showMoreDropDown: boolean;
}

interface AuthState {
  customerAuthEnabled: boolean;
  isLoggedIn: boolean;
  prevSession: React.MutableRefObject<boolean>;
  isCmaApp: boolean;
}

interface MerchantTypeFlags {
  isRetail: boolean;
  isRestaurant: boolean;
  isService: boolean;
  isHybrid: boolean;
  isEssential: boolean;
}

const renderWhen = (condition: boolean, content: ReactNode): ReactNode =>
  condition ? content : null;

// Helper function to determine merchant type flags
const getMerchantTypeFlags = (merchant: any): MerchantTypeFlags => ({
  isRetail: merchant.isRetail,
  isRestaurant: merchant.isRestaurant,
  isService: merchant.isService,
  isHybrid: merchant.isHybrid,
  isEssential: merchant.isEssentialMerchant,
});

// Determine if services link should render
const shouldShowServicesLink = (merchant: any): boolean =>
  merchant.isService && !merchant.hasOnlyRetailItems;

// Determine if shop link should render
const shouldShowShopLink = (merchant: any): boolean =>
  merchant.isHybrid || (merchant.isService && merchant.hasOnlyRetailItems);

// Determine if day part menu should render
const shouldShowDayPartMenu = (merchant: any): boolean =>
  merchant.isRestaurant || merchant.isEssentialMerchant;

// Determine if gift card link should render
const shouldShowGiftCardLink = (merchant: any, isDineIn: boolean): boolean =>
  merchant.isSellingGiftCards && !isDineIn;

// Determine if about link should render
const shouldShowAboutLink = (
  merchant: any,
  isDuda: boolean | undefined
): boolean => merchant.merchantMeta?.selectedSetup !== "Simple" && !isDuda;

// Determine if search icon should render
const shouldShowSearchIcon = (
  isPreview: boolean,
  pathName: string | null
): boolean => !isPreview && !pathName?.includes("/giftcard");

// Determine if utility panel should render
const shouldShowUtilityPanel = (
  isPreview: boolean,
  merchant: any,
  customerAuthEnabled: boolean,
  isScanToOrderFlow: boolean,
  isLoading: boolean
): boolean =>
  !isPreview &&
  merchant.isCheckoutEnabled &&
  customerAuthEnabled &&
  !isScanToOrderFlow &&
  !isLoading;

// Determine if restaurant cart should render
const shouldShowRestaurantCartDrawer = (
  shouldRenderRestaurantCart: boolean,
  tableData: any,
  isScanToOrderFlow: boolean
): boolean => !tableData && shouldRenderRestaurantCart && !isScanToOrderFlow;

// Determine if mobile category modal should render
const shouldShowMobileCategoryModal = (
  merchant: any,
  pathName: string | null
): boolean =>
  merchant.isRetail === false &&
  !!pathName &&
  (pathName.includes("all") || pathName.includes("shop"));

// Determine if category button should show
const shouldShowCategoryButton = (
  merchant: any,
  pathName: string | null
): boolean =>
  merchant.isRetail === false &&
  !!pathName &&
  (pathName.includes("services") ||
    pathName.includes("shop") ||
    pathName.includes("categories") ||
    pathName.includes("menu"));

// Get main wrapper classes
const getMainWrapperClasses = (
  merchant: any,
  pathName: string | null
): string => {
  const isNonRetailOnNonProductPage =
    !merchant.isRetail &&
    pathName !== null &&
    !pathName.includes("shop") &&
    !pathName.includes("services") &&
    !pathName.includes("categories") &&
    !pathName.includes("menu");

  return isNonRetailOnNonProductPage ? "" : "w-full";
};

export interface NavigationBarProps {
  menu: any;
  customerPerks: any;
  merchantPreferences: any;
  deleteSession?: any;
  retailCategories?: any;
  serviceCategories?: any;
  pickupTimePromise?: Promise<any>;
  defaultAddress: Address | null;
  isLoading: boolean;
  setIsCategoryMenuOpen: Dispatch<SetStateAction<boolean | undefined>>;
  isCategoryMenuOpen?: boolean;
  dayPartMenus?: DayPart[];
  dayPartMenuCategories?: DayPartMenuCategories[];
  selectedMenu?: DayPart | null;
  // eslint-disable-next-line no-unused-vars
  setSelectedMenu?: (menu: DayPart | null) => void;
  isNotAcceptingOrder: boolean;
  isScrolledDown?: boolean;
  isDuda?: boolean;
  restaurantMfeEnabled?: boolean;
  /** Keep shell-owned drawer portals mounted while another nav renders. */
  suppressNavigationUi?: boolean;
}

export const NavigationBar = ({
  menu,
  customerPerks,
  merchantPreferences,
  deleteSession,
  retailCategories,
  serviceCategories,
  pickupTimePromise,
  defaultAddress,
  isLoading,
  setIsCategoryMenuOpen,
  isCategoryMenuOpen,
  dayPartMenus,
  dayPartMenuCategories,
  selectedMenu = null,
  setSelectedMenu,
  isNotAcceptingOrder,
  isScrolledDown,
  isDuda,
  restaurantMfeEnabled = false,
  suppressNavigationUi = false,
}: NavigationBarProps) => {
  const t = useTranslations("NavigationBar");
  const { merchant } = useMerchant();
  const { recommendedCategory } = useRecommendedCategory();
  const pathName = usePathname();
  const { push, replace, refresh } = useRouter();
  const { isLoggedIn } = useAuth();
  const [cookies] = useCookies(["preview", "scanToOrderInfo"]);
  const { scanToOrderInfo } = cookies;
  const isScanToOrderFlow = !!scanToOrderInfo;
  const isDayPartsEnabled = merchant?.enabledSettings?.includes(
    "OLO_MENUS_DAY_PART_ENABLED"
  );
  const currentlySavedAddress = getFromLocalStorage<Address | null>(
    LocalStorageKey.DELIVERY_ADDRESS,
    null
  );
  const [sideMenuOpen, setSideMenuOpen] = useState<boolean>(false);
  const [searchFieldOpen, setSearchFieldOpen] = useState<boolean>(false);
  const [perksModalIsOpen, setPerksModalIsOpen] = useState<boolean>(false);
  const [loginModalIsOpen, setLoginModalIsOpen] = useState<boolean>(false);
  const { cart } = useCart();
  const storedCart = cart?.items || [];
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [tempAddress, setTempAddress] = useState(currentlySavedAddress ?? "");
  const [isClearCartOpen, setIsClearCartOpen] = useState<boolean>(false);
  const [menuState, setMenuState] = useState<Category[]>(menu);
  const [hoursModalIsOpen, setHoursModalIsOpen] = useState<boolean>(false);
  const [, setCustomerDetails] = useState<Partial<CustomerDetails> | null>(
    null
  );
  const [toCheckoutAfterLogin, setToCheckoutAfterLogin] =
    useState<boolean>(false);
  const [tableData, setTableData] = useState<TableData | null>();
  const [, setAddedToCartBannerShowing] =
    useState<boolean>(false);
  const merchantMeta = merchant.merchantMeta;
  const merchantColor = useMerchantColor();
  const searchParams = useSearchParams();

  const { isCmaApp, openLoginSignUp } = useNativeActions();
  const depositMightApply = useDepositPolicy(merchant, merchantPreferences);
  const getNavigationTestId = (testId: string) =>
    suppressNavigationUi ? undefined : applyDataTestId(testId);

  const [storedGiftCard] = useLocalStorage<GiftCard | null>(
    LocalStorageKey.GIFT_CARD,
    null
  );
  const shouldRenderRestaurantCart = merchant.isRestaurant;
  const shouldRenderRetailCart =
    merchant.isRetail ||
    merchant.isHybrid ||
    (merchant?.isService && merchant?.hasOnlyRetailItems) ||
    storedGiftCard;
  const showPerks = getShowPerks(
    merchant,
    merchantPreferences,
    depositMightApply
  );
  const customerAuthEnabled: boolean =
    process.env.NEXT_PUBLIC_AUTH_ENABLED === "true";
  const prevSession = useRef(isLoggedIn);
  const isUserInitiatedDayPartSwitch = useRef(false);

  const nonEssentialRestaurant =
    merchant.isRestaurant && merchant.isCheckoutEnabled;
  const nameToUseOnMenuLink = `${
    nonEssentialRestaurant ? t("menu") : t("categories")
  }`;
  const menuName = searchParams?.get("menu");
  const queryParams = buildNavQueryParams(scanToOrderInfo, menuName);

  const isDineIn =
    searchParams?.get("order_type")?.toLowerCase() ===
    OrderType.DINE_IN.toLowerCase();

  const tempMenu = useRef<DayPart | null>(null);

  const retailPickupTime = pickupTimePromise ? use(pickupTimePromise) : null;
  const isMobileScreen = useMediaQuery("(max-width: 640px)");

  const hasHorizontalScrollFlag = useMerchantFeatureFlag(
    merchant,
    FEATURE_FLAG_HORIZONTAL_SCROLL_ON_COLO2
  );

  useEffect(() => {
    if (
      isLoggedIn &&
      !currentlySavedAddress &&
      defaultAddress &&
      Object.keys(defaultAddress).length > 0
    ) {
      saveToLocalStorage(LocalStorageKey.DELIVERY_ADDRESS, defaultAddress);
      setTempAddress(defaultAddress);
    }
  }, [defaultAddress]);

  useAuthLifecycle({
    isLoggedIn,
    isDineIn,
    isScanToOrderFlow,
    deleteSession,
    refresh,
    merchantId: merchant.id,
    isMobileScreen,
    prevSession,
    setCustomerDetails,
  });

  let uncategorisedItems = useMemo(() => {
    return menuState?.find(
      (category: any) => category.id === UNCATEGORISED_CATEGORY_ID
    );
  }, [menuState]);

  let categories = useMemo(() => {
    if (!menuState) return [];

    return (
      Object.values(
        menuState.filter((item: any) => item.id !== UNCATEGORISED_CATEGORY_ID)
      ) as any[]
    ).sort((a: any, b: any) => {
      const orderA = (a as { sortOrder: number }).sortOrder;
      const orderB = (b as { sortOrder: number }).sortOrder;
      return orderA - orderB;
    });
  }, [menuState]);

  const uncategorized = {
    id: UNCATEGORISED_CATEGORY_ID,
    items: [],
    name: t("uncategorized"),
    sortOrder: Number.MAX_SAFE_INTEGER,
  };

  categories = insertUncategorisedCategory(
    categories,
    uncategorisedItems,
    uncategorized
  );

  // Memoized so downstream memos (mappedCategories) keep a stable reference.
  const baseCategories = categories;
  categories = useMemo(
    () =>
      insertRecommendedCategory(
        baseCategories,
        recommendedCategory ?? undefined
      ),
    [baseCategories, recommendedCategory]
  );

  let navigationCategories = merchantMeta?.navigationCategories;
  const mappedCategories: Category[] = useMemo(
    () =>
      getMappedNavigationCategories(categories, navigationCategories),
    [categories, navigationCategories]
  );

  const remainingCategories = categories.filter((category) => {
    return !mappedCategories?.some(
      (mappedCategory: any) => mappedCategory.id === category.id
    );
  });

  const moreCategories = [...remainingCategories];

  const serviceCart = ServiceCart.getItems();
  const retailCart = RetailCart.getItems();
  const restaurantCart = RestaurantCart.getItems();

  const {
    isRetailCartOpen,
    setIsRetailCartOpen,
    isRestaurantCartOpen,
    setIsRestaurantCartOpen,
    isServiceSummaryOpen: isAppointmentSummaryOpen,
    setIsServiceSummaryOpen: setIsAppointmentSummaryOpen,
    toggleCartDrawer: handleCartDrawer,
    isHandlingCartDrawer,
    setIsHandlingCartDrawer,
    isProceedingToCheckout,
    openCartDrawer,
  } = useCartDrawer();

  const [isScanToOrderKitchenModalOpen, setIsScanToOrderKitchenModalOpen] =
    useState<boolean>(false);
  const [deliveryAddressModalOpen, setDeliveryAddressModalOpen] =
    useState<boolean>(false);
  const [deliveryAddressErrorModalOpen, setDeliveryAddressErrorModalOpen] =
    useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  useNavigationBarEffects({
    pathName,
    isScanToOrderFlow,
    scanToOrderInfo,
    setSearchFieldOpen,
    setSearchQuery,
    setIsRetailCartOpen,
    setIsRestaurantCartOpen,
    setIsPreview,
    setTableData,
    setAddedToCartBannerShowing,
    navigationCategories,
    remainingCategories,
    mappedCategories,
    selectedMenu,
    dayPartMenuCategories,
    setMenuState,
    isUserInitiatedDayPartSwitch,
  });

  useCartDrawerRedirects({
    merchant,
    pathName,
    queryParams,
    serviceCartLength: serviceCart.length,
    retailCartLength: retailCart.length,
    storedGiftCard,
    isRestaurantCartOpen: Boolean(isRestaurantCartOpen),
    isAppointmentSummaryOpen,
    isRetailCartOpen,
    isHandlingCartDrawer,
    setIsHandlingCartDrawer,
    isProceedingToCheckout,
    replace,
    restaurantMfeEnabled,
  });
  const {
    onDayPartMenuChange,
    onConfirmation,
    handleLoginModal,
    handlePerksModal,
    handleHoursModal,
  } = useNavigationBarHandlers({
    isDineIn,
    restaurantCartLength: restaurantCart.length,
    selectedMenu,
    searchParams,
    setIsClearCartOpen,
    setSelectedMenu,
    isCmaApp,
    openLoginSignUp,
    loginModalIsOpen,
    setLoginModalIsOpen,
    setIsRetailCartOpen,
    setToCheckoutAfterLogin,
    perksModalIsOpen,
    setPerksModalIsOpen,
    hoursModalIsOpen,
    setHoursModalIsOpen,
    tempMenu,
    isUserInitiatedDayPartSwitch,
    restaurantMfeEnabled,
    isDayPartsEnabled,
  });

  const clearCartActions: ClearCartAction[] = [
    {
      text: t("cancel"),
      onClick: () => setIsClearCartOpen(false),
      type: "secondary",
    },
    {
      text: t("clearCartAndSwitchMenu"),
      onClick: onConfirmation,
      type: "primary",
    },
  ];

  useEffect(() => {
    if (!selectedMenu) return;
    const categories =
      dayPartMenuCategories?.find((o) => o.menuId === selectedMenu?.menuId)
        ?.categories || [];
    setMenuState(categories);

    if (!isUserInitiatedDayPartSwitch.current) return;

    // observes after day part menu page loads for the first category ins secondary navigation to focus
    const observer = new MutationObserver((mutations, obs) => {
      const firstSecondaryNavOption = document.getElementById(
        pom.accessibilityIds.secondaryNavStart
      );
      if (firstSecondaryNavOption) {
        firstSecondaryNavOption.focus();

        obs.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // after 5 seconds, stop checking body for first day part menu category in secondary navigation
    const timeoutId = setTimeout(() => {
      isUserInitiatedDayPartSwitch.current = false;
      observer.disconnect();
    }, 5000);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [selectedMenu]);

  useOrderEstimatePolling({
    merchantId: merchant.id,
    isRetailMerchant: merchant.isRetail,
    retailPickupDateTimeMillis: retailPickupTime?.pickupDateTimeMillis,
    isRetailCartOpen: Boolean(isRetailCartOpen),
    isRestaurantCartOpen: Boolean(isRestaurantCartOpen),
  });

  const handleSearch = useNavSearch({
    pathName,
    merchant,
    searchQuery,
    searchParams,
    push,
    setSearchFieldOpen,
    restaurantMfeEnabled,
  });

  const showMoreDropDown = useMemo(() => {
    return shouldShowMoreDropdown(moreCategories, pathName);
  }, [moreCategories, pathName]);

  const { cartValue, shouldShow } = useMemo(
    () => getCartStatus(storedCart, storedGiftCard, tableData),
    [storedCart, storedGiftCard, tableData]
  );

  const cartAriaLabel = getCartAriaLabel({
    shouldShow,
    merchant,
    cartValue,
    storedGiftCard,
    retailCartLength: retailCart.length,
    serviceCartLength: serviceCart.length,
    t,
  });

  const menuCategoriesText = `${t("menu")} ${t("categories")}`;
  const renderNavigationBar = () => ( // NOSONAR
    <>
      {renderWhen(isClearCartOpen, (
        <ClearCartModal
          isOpen={isClearCartOpen}
          onRequestClose={() => setIsClearCartOpen(false)}
          merchantColor={merchantColor}
          addToCart={onConfirmation}
          actions={clearCartActions}
          title={t("clearCartTitle")}
          message={t("clearCartMessage", {
            menuName: tempMenu.current?.menuName ?? "",
          })}
        />
      ))}
      <div
        className={clsx(
          "flex w-full items-center justify-between sticky sm:relative top-0 z-40",
          suppressNavigationUi && "hidden"
        )}
      >
        <div className="flex sm:hidden py-5 px-3 sm:px-8 ">
          <button
            type="button"
            onClick={() => setSideMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
            data-testid={getNavigationTestId(pom.header.hamburgerButton)}
          >
            <span className="sr-only">{t("openMainMenu")}</span>
            <Bars3Icon aria-hidden="true" className="h-6 w-6" />
          </button>
        </div>
        {renderWhen(searchFieldOpen, (
          <SearchField
            {...{
              searchParams,
              setSearchQuery,
              handleSearch,
              setSearchFieldOpen,
            }}
          />
        ))}
        {renderWhen(!searchFieldOpen, (
          <>
            {renderWhen(shouldShowCategoryButton(merchant, pathName), (
              <CategoryButton
                setIsCategoryMenuOpen={setIsCategoryMenuOpen}
                merchant={merchant}
                selectedDayPart={selectedMenu}
                suppressNavigationUi={suppressNavigationUi}
                restaurantCategories={categories}
                retailCategories={retailCategories}
                serviceCategories={serviceCategories}
              />
            ))}
            <div
              className={`flex flex-col w-full items-end sm:items-center justify-between ${getMainWrapperClasses(
                merchant,
                pathName
              )}`}
            >
              <nav
                aria-label={t("navigationBarLabel")}
                data-testid={getNavigationTestId(pom.header.navigationBar)}
                className={[
                  "flex",
                  "items-center",
                  "justify-between",
                  "sm:w-full",
                  `${isDuda ? "sm:pt-6 sm:px-6" : "sm:px-10"}`,
                  `${
                    !isDuda && !isMobileScreen && isScrolledDown ? "pb-3" : ""
                  }`,
                ].join(" ")}
              >
                <PopoverGroup
                  className={clsx(
                    "hidden",
                    "h-full",
                    "sm:flex",
                    !isDuda && !isMobileScreen && !isScrolledDown
                      ? "pt-5"
                      : "pt-0 gap-2.5"
                  )}
                  data-testid={getNavigationTestId(
                    pom.header.navigationLinksContainer
                  )}
                >
                  <div
                    className={clsx("transition-all duration-[1.4s] ease-out", {
                      "absolute left-[-9999px] max-w-0 opacity-0 invisible":
                        isDuda || (!isMobileScreen && !isScrolledDown),
                      "max-w-full":
                        !isDuda && !isMobileScreen && isScrolledDown,
                    })}
                    aria-hidden={!isMobileScreen && !isScrolledDown}
                  >
                    <MerchantLogo
                      showLink={
                        !isDineIn && !merchant.isStandaloneGiftCardWebsite
                      }
                      linkHref={ROUTE_HOME}
                      merchant={merchant}
                      merchantLogo={merchant.logo}
                    />
                  </div>

                  {renderWhen(shouldShowServicesLink(merchant), (
                    <NavLink
                      name={t("services")}
                      nameToUse="services/all"
                      merchant={merchant}
                      style={{ borderColor: merchantColor }}
                      showBottomBorder={pathName?.includes(ROUTE_SERVICES)}
                      data-testid={getNavigationTestId(
                        pom.navigationBar.servicesLink
                      )}
                      skipToID={pom.accessibilityIds.secondaryNavStart}
                    />
                  ))}
                  {renderWhen(shouldShowShopLink(merchant), (
                    <NavLink
                      name={t("shop")}
                      nameToUse="shop"
                      merchant={merchant}
                      style={{ borderColor: merchantColor }}
                      showBottomBorder={pathName?.includes(ROUTE_SHOP)}
                      data-testid={getNavigationTestId(pom.navigationBar.shopLink)}
                      skipToID={pom.accessibilityIds.secondaryNavStart}
                    />
                  ))}
                  {renderWhen(shouldShowDayPartMenu(merchant), (
                    <>
                      {dayPartMenus && dayPartMenus.length > 0 ? (
                        <DayPartMenuSelector
                          dayPartMenus={dayPartMenus}
                          selectedMenu={selectedMenu}
                          onMenuChange={onDayPartMenuChange}
                          merchant={merchant}
                          style={{ borderColor: merchantColor }}
                          showBottomBorder={
                            menuCategoriesText
                              .toLowerCase()
                              .includes(nameToUseOnMenuLink?.toLowerCase()) &&
                            pathName?.includes(
                              nameToUseOnMenuLink?.toLowerCase()
                            )
                          }
                        />
                      ) : (
                        <NavLink
                          name={`${nameToUseOnMenuLink}`}
                          nameToUse={`${nameToUseOnMenuLink.toLowerCase()}${ROUTE_ALL}${queryParams}`}
                          merchant={merchant}
                          style={{ borderColor: merchantColor }}
                          showBottomBorder={
                            menuCategoriesText
                              .toLowerCase()
                              .includes(nameToUseOnMenuLink?.toLowerCase()) &&
                            pathName?.includes(
                              nameToUseOnMenuLink?.toLowerCase()
                            )
                          }
                          skipToID={pom.accessibilityIds.secondaryNavStart}
                        />
                      )}
                    </>
                  ))}

                  {merchant.isRetail &&
                    mappedCategories?.map((c: { id: string; name: string }) => {
                      const categoryHeaderID = `${c.name.replaceAll(
                        /\s+/g,
                        "-"
                      )}-header`;
                      return (
                        <NavLink
                          key={c.id}
                          merchant={merchant}
                          nameToUse={
                            c.id === uncategorized.id
                              ? ROUTE_UNCATEGORISED
                              : encodeURIComponent(c.name)
                          }
                          name={encodeURIComponent(c.name)}
                          style={{ borderColor: merchantColor }}
                          showBottomBorder={false}
                          data-testid={getNavigationTestId(
                            pom.navigationBar.selectedCategories
                          )}
                          skipToID={categoryHeaderID}
                          basePath={ROUTE_RETAIL}
                          hoverBgColor={isDuda ? "transperant" : "gray-50"}
                        />
                      );
                    })}
                  {/* This component appears on URL match */}
                  {moreCategories.some(
                    (c: { id: string; name: string }) =>
                      decodeURIComponent(pathName || "").replace(
                        `/${
                          process.env
                            .NEXT_PUBLIC_ALLOW_SHOP_AS_DEFAULT_RETAIL_ROUTE ===
                          "true"
                            ? "shop/"
                            : ""
                        }`,
                        ""
                      ) === c.name
                  ) && (
                    <NavLink
                      name={(pathName || "")
                        .replace(
                          process.env
                            .NEXT_PUBLIC_ALLOW_SHOP_AS_DEFAULT_RETAIL_ROUTE
                            ? "/shop"
                            : "",
                          ""
                        )
                        .substring(1)}
                      merchant={merchant}
                      style={{ borderColor: merchantColor }}
                      showBottomBorder={
                        ((pathName || "").substring(1) === "services" &&
                          pathName?.includes(ROUTE_SERVICES)) ||
                        (menuCategoriesText.includes(
                          (pathName || "").substring(1)
                        ) &&
                          pathName?.includes(nameToUseOnMenuLink))
                      }
                      skipToID={`${pathName.replaceAll(/\s+/g, "-")}-header`}
                      basePath={ROUTE_RETAIL}
                      hoverBgColor={isDuda ? "transperant" : "gray-50"}
                    />
                  )}
                  {renderWhen(merchant.isRetail && showMoreDropDown, (
                    <Popover
                      className="relative"
                      data-testid={getNavigationTestId(
                        pom.navigationBar.moreDropdown
                      )}
                    >
                      <PopoverButton
                        className={`w-full h-full capitalize leading-6 px-4 text-center pb-3 text-[16px] flex gap-x-2 items-center link ${
                          isDuda ? "hover:bg-transparent" : "hover:bg-gray-50"
                        }`}
                      >
                        {t("more")}
                        <ChevronDownIcon
                          aria-hidden="true"
                          className="h-5 w-5 flex-none"
                        />
                      </PopoverButton>
                      <PopoverPanel
                        transition
                        className="navbar-dropdown-scrollbar absolute -left-8 top-full z-20 mt-3 w-screen max-w-[400px] overflow-x-hidden overflow-y-auto rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in max-h-[500px] px-6 py-5"
                      >
                        {moreCategories
                          .filter(
                            (c: { id: string; name: string }) =>
                              decodeURIComponent(pathName || "").replace(
                                "/",
                                ""
                              ) !== c.name
                          )
                          .map((c: { id: string; name: string }) => {
                            const categoryHref =
                              c.id === uncategorized.id
                                ? `${ROUTE_RETAIL}/${ROUTE_UNCATEGORISED}`
                                : `${ROUTE_RETAIL}/${encodeURIComponent(c.name)}`;

                            return (
                              <CloseButton
                                as={Link}
                                key={c.id}
                                href={categoryHref}
                                className="group relative flex items-center gap-x-6 rounded-lg py-3 text-normal leading-6 hover:bg-gray-50 text-left w-full"
                                aria-label={`${c.name},`}
                                onClick={(event) => {
                                  if (shouldHardNavigate(categoryHref, merchant)) {
                                    event.preventDefault();
                                    globalThis.location.assign(categoryHref);
                                  }
                                }}
                              >
                                {c.name}
                              </CloseButton>
                            );
                          })}
                      </PopoverPanel>
                    </Popover>
                  ))}
                  {renderWhen(shouldShowGiftCardLink(merchant, isDineIn), (
                    <NavLink
                      name={t("giftCard")}
                      nameToUse="giftcard"
                      merchant={merchant}
                      style={{ borderColor: merchantColor }}
                      showBottomBorder={pathName?.includes("/giftcard")}
                      data-testid={getNavigationTestId(pom.giftCard.navLink)}
                      hoverBgColor={isDuda ? "transperant" : "gray-50"}
                    />
                  ))}

                  {renderWhen(shouldShowAboutLink(merchant, isDuda), (
                    <NavLink
                      name={t("about")}
                      nameToUse="about"
                      merchant={merchant}
                      style={{ borderColor: merchantColor }}
                      showBottomBorder={pathName?.includes("/about")}
                      queryParams={queryParams}
                      data-testid={getNavigationTestId(
                        pom.navigationBar.aboutSection
                      )}
                    />
                  ))}
                </PopoverGroup>
                <div
                  className={[
                    "flex",
                    "flex-row",
                    "gap-5",
                    "items-center",
                    "mr-4",
                  ].join(" ")}
                >
                  {renderWhen(shouldShowPerksPanel(
                    isLoggedIn,
                    customerPerks,
                    showPerks,
                    merchant,
                    tableData,
                    isCmaApp
                  ), (
                    <Perks
                      {...{
                        handlePerksModal,
                        perksModalIsOpen,
                        customerPerks,
                        suppressNavigationUi,
                      }}
                    />
                  ))}
                  {renderWhen(shouldShowSearchIcon(isPreview, pathName), (
                    <button
                      className="cursor-pointer border-none"
                      onClick={() => setSearchFieldOpen(true)}
                      type="button"
                      aria-label={t("globalSearchDescriptiveText")}
                    >
                      <SearchIcon />
                    </button>
                  ))}
                  <BusinessHoursModal
                    isOpen={hoursModalIsOpen}
                    onRequestClose={handleHoursModal}
                    businessHours={merchant?.businessHours?.hours}
                    timezone={merchant.timezone}
                  />
                  {shouldShowUtilityPanel(
                    isPreview,
                    merchant,
                    customerAuthEnabled,
                    isScanToOrderFlow,
                    isLoading
                  ) &&
                    (isLoading ? (
                      <IconSkeleton width={20} height={20} />
                    ) : (
                      <UtilityPanel
                        {...{
                          handleLoginModal,
                          loginModalIsOpen,
                          setLoginModalIsOpen,
                          deleteSession,
                          merchantColor,
                          toCheckoutAfterLogin,
                        }}
                        merchant={merchant}
                      />
                    ))}
                  {renderWhen(merchant.isCheckoutEnabled && !isPreview, (
                    <button
                      className="cursor-pointer relative inline-flex sm:mr-0"
                      onClick={handleCartDrawer}
                      data-testid={getNavigationTestId(pom.landingPage.cartIcon)}
                      aria-label={cartAriaLabel}
                      type="button"
                    >
                      <CartIcon />
                      <CartNumber
                        merchantColor={merchantColor}
                        cartValue={cartValue}
                        shouldShow={shouldShow}
                        suppressNavigationUi={suppressNavigationUi}
                      />
                    </button>
                  ))}
                  {renderWhen(Boolean(shouldRenderRetailCart), (
                    <RetailCartDrawer
                      isOpen={isRetailCartOpen}
                      onClose={() => setIsRetailCartOpen(false)}
                      onRequestClose={handleCartDrawer}
                      merchant={merchant}
                      onTriggerLoginModal={handleLoginModal}
                      retailPickupTime={retailPickupTime}
                      setDeliveryAddressModalOpen={setDeliveryAddressModalOpen}
                      setIsRetailCartOpen={
                        setIsRetailCartOpen as (
                          value?: boolean | undefined
                        ) => void
                      }
                      setDeliveryAddressErrorModalOpen={
                        setDeliveryAddressErrorModalOpen
                      }
                    />
                  ))}
                  <ServiceSummary
                    isOpen={isAppointmentSummaryOpen}
                    onClose={() => setIsAppointmentSummaryOpen(false)}
                    onRequestClose={handleCartDrawer}
                    merchant={merchant}
                    onTriggerLoginModal={handleLoginModal}
                    merchantPreferences={merchantPreferences}
                    merchantColor={merchantColor}
                    setIsRetailCartOpen={setIsAppointmentSummaryOpen}
                  />
                  {renderWhen(shouldShowRestaurantCartDrawer(
                    shouldRenderRestaurantCart,
                    tableData,
                    isScanToOrderFlow
                  ), (
                    <RestaurantCartDrawer
                      isOpen={isRestaurantCartOpen}
                      onClose={() => setIsRestaurantCartOpen(false)}
                      onRequestClose={handleCartDrawer}
                      merchant={merchant}
                      onTriggerLoginModal={handleLoginModal}
                      setDeliveryAddressModalOpen={setDeliveryAddressModalOpen}
                      setIsRestaurantCartOpen={setIsRestaurantCartOpen}
                      setDeliveryAddressErrorModalOpen={
                        setDeliveryAddressErrorModalOpen
                      }
                      selectedDayPart={selectedMenu}
                    />
                  ))}
                  {tableData && (
                    <ScanToOrderCart
                      isOpen={isRestaurantCartOpen}
                      onRequestClose={handleCartDrawer}
                      merchant={merchant}
                      tableData={tableData}
                      setIsSentToKitchen={setIsScanToOrderKitchenModalOpen}
                      scanToOrderInfo={scanToOrderInfo}
                      isRunningTabsEnabled={merchant.isRunningTabsEnabled}
                      selectedDayPart={selectedMenu}
                    />
                  )}
                  <ScanToOrderKitchenModal
                    isOpen={isScanToOrderKitchenModalOpen}
                    onRequestClose={() =>
                      setIsScanToOrderKitchenModalOpen(false)
                    }
                    merchant={merchant}
                    scanToOrderInfo={scanToOrderInfo}
                  />
                  <EditAddressesModal
                    setDefaultDeliveryAllowed={isLoggedIn}
                    isOpen={deliveryAddressModalOpen}
                    onRequestClose={() => setDeliveryAddressModalOpen(false)}
                    onUpdated={async (
                      address: any,
                      isDefaultDelivery: boolean
                    ) => {
                      setTempAddress(address);
                      await onDeliveryAddressUpdate(
                        address,
                        isDefaultDelivery,
                        merchant.id,
                        {
                          openCartDrawer,
                          setDeliveryAddressModalOpen,
                          setDeliveryAddressErrorModalOpen,
                        }
                      );
                    }}
                    checkoutCloseHandler={() => {}}
                  />
                  <DeliveryModalError
                    onRequestClose={() =>
                      setDeliveryAddressErrorModalOpen(false)
                    }
                    isOpen={deliveryAddressErrorModalOpen}
                    address={tempAddress}
                    onEnterNewAddress={() => {
                      setDeliveryAddressErrorModalOpen(false);
                      setDeliveryAddressModalOpen(true);
                    }}
                    checkoutCloseHandler={() => {}}
                  />
                </div>
              </nav>
              <Divider classNames="w-[calc(100%-80px)]"></Divider>
              {!isMobileScreen && (
                <SecondaryNavigation
                  pathName={pathName}
                  merchant={merchant}
                  hasHorizontalScrollFlag={hasHorizontalScrollFlag}
                  retailCategories={retailCategories}
                  serviceCategories={serviceCategories}
                  isScrolledDown={isScrolledDown}
                  mappedCategories={mappedCategories}
                  moreCategories={moreCategories}
                  scanToOrderInfo={scanToOrderInfo}
                  isNotAcceptingOrder={isNotAcceptingOrder}
                  categories={categories}
                  restaurantMfeEnabled={restaurantMfeEnabled}
                />
              )}
            </div>
          </>
        ))}
        <MobileNavigationBar
          categories={categories}
          sideMenuOpen={sideMenuOpen}
          setSideMenuOpen={setSideMenuOpen}
          scanToOrderInfo={scanToOrderInfo}
          merchant={merchant}
          restaurantMfeEnabled={restaurantMfeEnabled}
          dayPartMenus={dayPartMenus}
          onDayPartMenuChange={onDayPartMenuChange}
          selectedMenu={selectedMenu}
        />
        {renderWhen(shouldShowMobileCategoryModal(merchant, pathName), (
          <MobileCategoryModal
            menu={categories}
            setIsCategoryMenuOpen={setIsCategoryMenuOpen}
            isCategoryMenuOpen={isCategoryMenuOpen}
            merchant={merchant}
            serviceCategories={serviceCategories}
            retailCategories={retailCategories}
          />
        ))}
      </div>
    </>
  );

  return renderNavigationBar();
};
